import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useKeycloakStore } from './keycloak.js'
import api from '../api/client.js'

export const useGroupsStore = defineStore('group', () => {
  const groups = ref([])
  const activeGroup = ref(null)
  const loading = ref(false)
  // Spring Data page metadata for the groups list (GET /groups is paged)
  const groupsPage = ref({ total: 0, number: 0, size: 20, last: true })

  const groupsByCategory = computed(
    () => (category) => groups.value.filter((g) => g.category === category),
  )

  // Accepts either a raw array or a Spring Data Page ({content, totalElements, ...}).
  function applyGroupsPage(data, append = false) {
    const content = Array.isArray(data) ? data : (data?.content ?? [])
    if (append) {
      const seen = new Set(groups.value.map((g) => g?.id))
      groups.value.push(...content.filter((g) => g && !seen.has(g.id)))
    } else {
      groups.value = content
    }
    groupsPage.value = {
      total: data?.totalElements ?? groups.value.length,
      number: data?.number ?? 0,
      size: data?.size ?? 20,
      last: data?.last !== false,
    }
  }

  async function fetchGroups() {
    loading.value = true
    try {
      const { data } = await api.get('groups/my-groups')
      console.log('Fetched groups:', data);
      applyGroupsPage(data)
    } finally {
      loading.value = false
    }
  }

  async function loadMoreGroups() {
    if (groupsPage.value.last) return
    const { data } = await api.get('groups/my-groups', {
      params: { page: groupsPage.value.number + 1, size: groupsPage.value.size },
    })
    applyGroupsPage(data, true)
  }

  async function fetchMyCreatedGroups() {
    loading.value = true
    try {
      const { data } = await api.get('groups/my-created-groups')
      applyGroupsPage(data)
    } finally {
      loading.value = false
    }
  }

  async function fetchGroupDetail(groupId) {
    const path = `groups/${groupId}`
    const { data } = await api.get(path)
    activeGroup.value = data
  }

  async function createGroup(payload) {
    const path = 'groups'
    try {
      const { data } = await api.post(path, payload)
      groups.value.unshift(data)
      return data
    } catch (e) {
      console.error('API createGroup error:', e)
      console.error('API response data:', e?.response?.data)
      console.error('API response status:', e?.response?.status)
      throw e
    }
  }

  async function updateGroup(groupId, payload) {
    const path = `groups/${groupId}`
    // The backend only exposes PUT (no PATCH) and requires the group name.
    // Merge the cached group so partial payloads stay valid and the backend
    // boolean flags are not reset to their defaults.
    const cached =
      activeGroup.value || groups.value.find((g) => g.id === groupId) || {}
    const body = {
      ...cached,
      ...payload,
      name: payload.name || cached.name,
    }
    const { data } = await api.put(path, body)
    activeGroup.value = data
    const index = groups.value.findIndex((g) => g.id === data.id)
    if (index !== -1) groups.value[index] = data
    return data
  }

  // Marketplace enable/disable is persisted as the group's marketplace mode
  // (ModeBehavior enum) through the dedicated backend endpoint.
  async function updateMarketplaceMode(groupId, mode) {
    const { data } = await api.put(`groups/${groupId}/marketplace-mode`, {
      marketplaceMode: mode,
    })
    activeGroup.value = data
    const index = groups.value.findIndex((g) => g.id === data.id)
    if (index !== -1) groups.value[index] = data
    return data
  }

  // ── Progressive Finance Enablement ────────────────────────────────────

  async function publishGroup(groupId) {
    const { data } = await api.post(`/groups/${groupId}/publish`)
    activeGroup.value = data
    const index = groups.value.findIndex((g) => g.id === data.id)
    if (index !== -1) groups.value[index] = data
    return data
  }

  async function assessFinancialReadiness(groupId, feature) {
    const { data } = await api.get(`/groups/${groupId}/financial-readiness/${feature}`)
    return data
  }

  async function enableFinancialFeature(groupId, feature) {
    const { data } = await api.post(`/groups/${groupId}/financial-features/${feature}/enable`)
    activeGroup.value = data
    const index = groups.value.findIndex((g) => g.id === data.id)
    if (index !== -1) groups.value[index] = data
    return data
  }

  async function disableFinancialFeature(groupId, feature) {
    const { data } = await api.post(`/groups/${groupId}/financial-features/${feature}/disable`)
    activeGroup.value = data
    const index = groups.value.findIndex((g) => g.id === data.id)
    if (index !== -1) groups.value[index] = data
    return data
  }

  async function updatePayoutConfigStatus(groupId, status) {
    const { data } = await api.put(`/groups/${groupId}/payout-config-status`, null, {
      params: { status }
    })
    activeGroup.value = data
    const index = groups.value.findIndex((g) => g.id === data.id)
    if (index !== -1) groups.value[index] = data
    return data
  }

  async function updateFinancialVerificationStatus(groupId, status) {
    const { data } = await api.put(`/groups/${groupId}/financial-verification-status`, null, {
      params: { status }
    })
    activeGroup.value = data
    const index = groups.value.findIndex((g) => g.id === data.id)
    if (index !== -1) groups.value[index] = data
    return data
  }

  async function setupPayoutConfiguration(groupId, payload) {
    const { data } = await api.post(`/groups/${groupId}/payout-setup`, payload)
    activeGroup.value = data
    const index = groups.value.findIndex((g) => g.id === data.id)
    if (index !== -1) groups.value[index] = data
    return data
  }

  return {
    groups,
    activeGroup,
    loading,
    groupsPage,
    groupsByCategory,
    fetchGroups,
    loadMoreGroups,
    fetchMyCreatedGroups,
    fetchGroupDetail,
    createGroup,
    updateGroup,
    updateMarketplaceMode,
    publishGroup,
    assessFinancialReadiness,
    enableFinancialFeature,
    disableFinancialFeature,
    updatePayoutConfigStatus,
    updateFinancialVerificationStatus,
    setupPayoutConfiguration,
  }
})
